import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import { 
  Package, Plus, Search, Clock, CheckCircle, XCircle, 
  Tag, Box, FileText, ChevronRight, AlertCircle, Layers,
  Upload, Paperclip, Trash2, Download, File, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ImportBrandBomTab from "@/components/ImportBrandBomTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories that support artwork file uploads
const ARTWORK_CATEGORIES = ["LB", "BS", "PM"];

// Aug 2026 — categories that DO NOT use the legacy brand-tagging
// picker. PM/BS use `buyer_sku_links[]` (with consumption); LB uses
// a single `category_data.buyer_sku` field. Both the validation
// guard (`handleCreateRmRequest`) and the render branch consume
// this set, so they can't drift again. Adding a future category to
// this club is a one-line change.
const NO_BRAND_TAGGING = new Set(["PM", "BS", "LB"]);

// ─── Feb 2026 (PM revamp) — shared helper components ─────────────────

// Lightweight searchable combobox. Not using Shadcn's `Command` here
// to avoid composer/portal wiring — a simple filter + scroll list is
// enough for a few hundred options and keeps the component self-contained.
function PmSearchSelect({ value, onChange, options, placeholder, testId, disabled }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o =>
      (o.label || o.value || "").toLowerCase().includes(needle)
    );
  }, [q, options]);
  const selectedLabel = options.find(o => o.value === value)?.label || value || "";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(v => !v)}
        className="w-full h-9 px-3 text-sm text-left border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
        data-testid={testId}
        disabled={disabled}
      >
        {selectedLabel || <span className="text-gray-400">{placeholder}</span>}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-white border rounded-md shadow-lg">
          <div className="sticky top-0 bg-white border-b p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to search..."
              className="w-full h-8 px-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid={`${testId}-search`}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-3 text-xs text-gray-400">No matches</div>
          ) : filtered.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value, opt); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${opt.value === value ? "bg-primary/10 font-medium" : ""}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Specs picker on PM — same list UX as PmSearchSelect but with an extra
// "Others" row that opens a text input. On confirm the new value hits
// the backend append endpoint (dedupes case-insensitively server-side),
// gets appended to the local options list, and becomes the selection.
function PmSpecsSelect({ value, onChange, options, onOptionsChange, testId }) {
  const [showOther, setShowOther] = React.useState(false);
  const [otherValue, setOtherValue] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const appendOption = async () => {
    const v = (otherValue || "").trim();
    if (!v) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/rm-categories/PM/columns/specs/options`,
        { value: v },
      );
      const canonical = res.data?.value || v;
      const nextOptions = res.data?.options || [...(options || []), canonical];
      onOptionsChange?.(nextOptions);
      onChange(canonical);
      setShowOther(false);
      setOtherValue("");
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to add option");
    } finally {
      setSubmitting(false);
    }
  };
  const opts = [
    ...(options || []).map(o => ({ value: o, label: o })),
    { value: "__other__", label: "Others - please specify" },
  ];
  return (
    <>
      <PmSearchSelect
        value={value}
        onChange={(v) => {
          if (v === "__other__") { setShowOther(true); return; }
          onChange(v);
        }}
        options={opts}
        placeholder="Select specification..."
        testId={testId}
      />
      {showOther && (
        <div className="mt-2 flex gap-2" data-testid={`${testId}-other-row`}>
          <input
            autoFocus
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
            placeholder="Type new spec..."
            className="flex-1 h-9 px-3 text-sm border rounded-md"
            data-testid={`${testId}-other-input`}
          />
          <button
            type="button"
            onClick={appendOption}
            disabled={submitting || !otherValue.trim()}
            className="h-9 px-3 text-sm bg-primary text-white rounded-md disabled:opacity-50"
            data-testid={`${testId}-other-add`}
          >
            {submitting ? "Adding..." : "Add"}
          </button>
          <button
            type="button"
            onClick={() => { setShowOther(false); setOtherValue(""); }}
            className="h-9 px-3 text-sm border rounded-md"
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}

// Buyer SKU multi-linker for PM — pick list + per-link consumption/UOM.
function PmBuyerSkuLinks({ links, onChange, buyerSkus, brandId }) {
  const [q, setQ] = React.useState("");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const linkedIds = new Set(links.map(l => l.buyer_sku_id));
  // Feb 2026 (P1) — brand-scoped Buyer SKU picker. When a brand is
  // selected on the PM form, only that brand's Buyer SKUs are pickable
  // — prevents cross-brand BOM linkage (packaging tied to Brand X
  // shouldn't ever land on Brand Y's Buyer SKU BOM). Empty brand →
  // legacy behaviour (all Buyer SKUs).
  const available = React.useMemo(() => {
    let list = buyerSkus.filter(s => !linkedIds.has(s.buyer_sku_id));
    if (brandId) {
      list = list.filter(s => s.brand_id === brandId);
    }
    const needle = q.trim().toLowerCase();
    return needle
      ? list.filter(s =>
          (s.buyer_sku_id || "").toLowerCase().includes(needle) ||
          (s.name || "").toLowerCase().includes(needle))
      : list;
  }, [buyerSkus, linkedIds, q, brandId]);

  const addLink = (sku) => {
    onChange([...links, { buyer_sku_id: sku.buyer_sku_id, consumption: 1, uom: "nos" }]);
    setPickerOpen(false); setQ("");
  };
  const updateLink = (buyerSkuId, patch) => {
    onChange(links.map(l => l.buyer_sku_id === buyerSkuId ? { ...l, ...patch } : l));
  };
  const removeLink = (buyerSkuId) => {
    onChange(links.filter(l => l.buyer_sku_id !== buyerSkuId));
  };

  return (
    <div data-testid="pm-buyer-sku-links">
      <Label>Link to Buyer SKUs *</Label>
      <div className="mt-2 space-y-2">
        {links.length === 0 && (
          <div className="p-3 border border-dashed rounded-md text-xs text-gray-400">
            No Buyer SKUs linked yet. Click Add Buyer SKU below.
          </div>
        )}
        {links.map(l => {
          const sku = buyerSkus.find(s => s.buyer_sku_id === l.buyer_sku_id);
          return (
            <div key={l.buyer_sku_id} className="p-3 border rounded-md bg-white flex items-center gap-3" data-testid={`pm-link-${l.buyer_sku_id}`}>
              <div className="flex-1">
                <div className="font-mono text-xs">{l.buyer_sku_id}</div>
                <div className="text-xs text-gray-500">{sku?.name || ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <Label className="text-[10px] text-gray-500">Consumption</Label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={l.consumption}
                    onChange={(e) => updateLink(l.buyer_sku_id, { consumption: parseFloat(e.target.value) || 0 })}
                    className="h-8 w-24 px-2 text-sm border rounded-md"
                    data-testid={`pm-link-consumption-${l.buyer_sku_id}`}
                  />
                </div>
                <div className="flex flex-col">
                  <Label className="text-[10px] text-gray-500">UOM</Label>
                  <input
                    type="text"
                    value={l.uom}
                    onChange={(e) => updateLink(l.buyer_sku_id, { uom: e.target.value })}
                    className="h-8 w-16 px-2 text-sm border rounded-md"
                    data-testid={`pm-link-uom-${l.buyer_sku_id}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeLink(l.buyer_sku_id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
        <div className="relative">
          <button
            type="button"
            onClick={() => setPickerOpen(v => !v)}
            className="h-9 px-3 text-sm border rounded-md hover:bg-gray-50"
            data-testid="pm-add-buyer-sku-btn"
          >
            + Add Buyer SKU
          </button>
          {pickerOpen && (
            <div className="absolute z-40 mt-1 w-96 max-h-80 overflow-y-auto bg-white border rounded-md shadow-lg">
              <div className="sticky top-0 bg-white border-b p-2">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search Buyer SKU..."
                  className="w-full h-8 px-2 text-sm border rounded-md"
                  data-testid="pm-buyer-sku-search"
                />
              </div>
              {available.length === 0 ? (
                <div className="p-3 text-xs text-gray-400">No matches</div>
              ) : available.slice(0, 100).map(sku => (
                <button
                  key={sku.buyer_sku_id}
                  type="button"
                  onClick={() => addLink(sku)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100"
                >
                  <div className="font-mono text-xs">{sku.buyer_sku_id}</div>
                  {sku.name && <div className="text-xs text-gray-500">{sku.name}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// Default fallback categories (used only if API fails)
const DEFAULT_RM_CATEGORIES = {
  // Jul 2026 (LB revamp) — `type` is now an admin-curated dropdown
  // (rendered via the generic `type: "select"` branch when options
  // are present). `unit` and `per_unit_weight` are dropped from LB;
  // brand tagging is replaced by a single Buyer SKU picker + Consumption
  // (see handleCreateRmRequest + field renderer below).
  "LB": {
    name: "Labels",
    // Jul 2026 — caption copy; DB `description` overrides this at runtime.
    description: "MRP, PO labels",
    nameFormat: ["type", "buyer_sku"],
    fields: [
      {
        key: "type",
        label: "Type",
        required: true,
        placeholder: "Select label type…",
        type: "select",
        options: [
          "Main Label",
          "Barcode Sticker",
          "MRP / Price Sticker",
          "Warning Label",
          "Age Grade Label",
          "Safety Warning Label",
          "Batch / Date Sticker",
          "Brand Logo Label",
          "Care / Wash Label",
          "QR Code Sticker",
          "Carton Label",
        ],
      },
      { key: "buyer_sku", label: "Buyer SKU", required: true, placeholder: "Select Buyer SKU…" },
    ]
  },
  "PM": { 
    name: "Packaging", 
    // Jul 2026 — caption copy; DB `description` overrides this at runtime.
    description: "Inner Box, Master Carton",
    nameFormat: ["model", "type", "specs", "brand"],
    fields: [
      { key: "model", label: "Model", required: true, placeholder: "e.g., Kids Scooter Box" },
      { key: "type", label: "Type", required: true, placeholder: "e.g., Inner Box, Outer Carton" },
      { key: "specs", label: "Specifications", required: true, placeholder: "e.g., 45x30x20 cm, 5-ply" },
      { key: "brand", label: "Brand", required: true, placeholder: "e.g., Baybee, Firstcry" },
      { key: "per_unit_weight", label: "Weight (grams)", required: false, placeholder: "e.g., 500", type: "number" },
      { key: "unit", label: "Unit", required: false, placeholder: "e.g., pcs" }
    ]
  },
  "BS": { 
    name: "Brand Assets", 
    // Jul 2026 — caption copy; DB `description` overrides this at runtime.
    description: "Product stickers, Manuals, Inserts",
    // Jul 2026 (BS revamp) — nameFormat drops the trailing free-text
    // `buyer_sku` (superseded by the multi Buyer SKU linker). Position
    // is optional, so `generateRmName`'s empty-skip omits it when
    // unset.
    // Feb 2026 (BS Model) — prepend optional `model` as the leading
    // identity segment so brand assets can be disambiguated by
    // product model (manuals/inserts are model-specific).
    // Aug 2026 (BS Variant) — append mandatory `variant_name` so
    // two artworks that share model/position/type/brand don't
    // collapse onto a single RM. Uses key `variant_name` (NOT
    // `name`) because the dedup signature builder in
    // `rm_routes.build_rm_signature` skips fields keyed literally
    // `name`. DB-driven config (`rm_categories.BS.description_columns`)
    // is authoritative — this fallback matches so the offline shape
    // stays in sync.
    nameFormat: ["model", "position", "type", "brand", "variant_name"],
    fields: [
      { key: "model", label: "Model", required: false, placeholder: "Select model…" },
      {
        key: "position", label: "Position/Location", required: false,
        placeholder: "Select position…",
        type: "select",
        options: [
          "Inside Box", "On Product", "On Carton", "On Polybag",
          "Hang Tag", "Front of Pack", "Back of Pack", "User Manual Slot",
        ],
      },
      {
        key: "type", label: "Type", required: true,
        placeholder: "Select type…",
        type: "select",
        options: [
          "User Manual", "Warranty Card", "Instruction Insert",
          "Safety Booklet", "Assembly Guide", "Brand Sticker",
          "Promotional Insert", "Thank You Card", "Certificate",
          "Barcode Sticker",
        ],
      },
      { key: "brand", label: "Brand", required: false, placeholder: "Select brand…" },
      {
        key: "variant_name", label: "Variant Name", required: true,
        placeholder: "e.g. Blue Rocket Artwork v2",
      },
    ]
  }
};

// Helper to convert database category to frontend format
const convertDbCategoryForDemandHub = (dbCat) => {
  const fields = (dbCat.description_columns || []).map(col => ({
    key: col.key,
    label: col.label || col.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    required: col.required || false,
    placeholder: col.placeholder || `Enter ${col.label || col.key}`,
    type: col.type || 'text'
  }));
  
  const nameFormat = (dbCat.description_columns || [])
    .filter(col => col.include_in_name)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(col => col.key);
  
  return {
    name: dbCat.name,
    description: dbCat.description || "",
    nameFormat: nameFormat,
    fields: fields,
    default_uom: dbCat.default_uom,
    rm_id_prefix: dbCat.rm_id_prefix
  };
};

// Generate RM name from category_data based on nomenclature
const generateRmName = (category, categoryData, rmCategories) => {
  const config = rmCategories[category];
  if (!config || !config.nameFormat || config.nameFormat.length === 0) return "";
  
  const parts = config.nameFormat
    .map(key => categoryData[key] || "")
    .filter(val => val.trim() !== "");
  
  return parts.join("_");
};

const DemandHub = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState("buyer-sku");
  
  // Data
  const [summary, setSummary] = useState({ total_pending: 0, total_approved: 0 });
  const [myRequests, setMyRequests] = useState([]);
  const [bidsoSkus, setBidsoSkus] = useState([]);
  const [brands, setBrands] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [rmCategoriesMap, setRmCategoriesMap] = useState(DEFAULT_RM_CATEGORIES);
  const [rmCategoriesList, setRmCategoriesList] = useState([]);
  
  // Loading
  const [loading, setLoading] = useState(false);
  
  // Filters for Bidso SKU lookup
  const [skuFilters, setSkuFilters] = useState({
    vertical_id: "",
    model_id: "",
    search: ""
  });
  
  // Selected Bidso SKU for Buyer SKU request
  const [selectedBidso, setSelectedBidso] = useState(null);
  const [existingBuyerSkus, setExistingBuyerSkus] = useState({ existing: [], pending_requests: [] });
  
  // Buyer SKU Request Form
  const [buyerSkuForm, setBuyerSkuForm] = useState({
    bidso_sku_id: "",
    brand_id: "",
    notes: ""
  });
  
  // RM Request Form
  const [rmForm, setRmForm] = useState({
    category: "LB",
    description: "",
    brand_ids: [],
    category_data: {},
    artwork_files: [],
    // PM (Feb 2026) — per-Buyer-SKU consumption. Non-PM categories ignore it.
    buyer_sku_links: [],
  });

  // Buyer SKU catalog for the PM linker. Loaded on mount.
  const [availableBuyerSkusForPm, setAvailableBuyerSkusForPm] = useState([]);
  // Aug 2026 — extracted so the loader can also fire when the Import
  // Brand BOM tab is (re-)opened, ensuring newly-created Buyer SKUs
  // appear in the target picker without a full page reload. Universe
  // limit raised from 5000 → 50000 (backend's `le=50000` cap) so the
  // whole active catalog loads deterministically after the sort-by-
  // buyer_sku_id fix at `/api/buyer-skus`.
  const loadBuyerSkusForPm = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buyer-skus`, {
        params: { status: "ACTIVE", limit: 50000 },
      });
      setAvailableBuyerSkusForPm(res.data?.items || res.data || []);
    } catch (err) {
      console.error("Failed to load buyer SKUs for PM linker", err);
    }
  }, []);
  useEffect(() => {
    loadBuyerSkusForPm();
  }, [loadBuyerSkusForPm]);
  // Refetch on entering the Import Brand BOM tab so a Buyer SKU
  // created earlier in the session becomes selectable as a target
  // without a browser reload. Guarded by `activeTab` so we don't
  // re-hit the endpoint on every render.
  useEffect(() => {
    if (activeTab === "import-brand-bom") {
      loadBuyerSkusForPm();
    }
  }, [activeTab, loadBuyerSkusForPm]);
  
  // File upload state
  const fileInputRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  // Buyer SKU search for RM form
  const [buyerSkuSearch, setBuyerSkuSearch] = useState([]);
  
  // Dialogs
  const [showBuyerSkuDialog, setShowBuyerSkuDialog] = useState(false);
  const [showRmDialog, setShowRmDialog] = useState(false);

  useEffect(() => {
    fetchMasterData();
    fetchSummary();
    fetchMyRequests();
  }, []);

  useEffect(() => {
    if (activeTab === "buyer-sku") {
      fetchBidsoSkus();
    }
  }, [activeTab, skuFilters]);

  const fetchMasterData = async () => {
    try {
      const [brandsRes, verticalsRes, modelsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/verticals`),
        axios.get(`${API}/models`),
        axios.get(`${API}/rm-categories`)
      ]);
      setBrands(brandsRes.data.filter(b => b.status === 'ACTIVE'));
      setVerticals(verticalsRes.data.filter(v => v.status === 'ACTIVE'));
      setModels(modelsRes.data.filter(m => m.status === 'ACTIVE'));
      
      // API returns categories in format: {code: {name, fields: string[], nameFormat: string[]}}
      const dbCategories = categoriesRes.data || {};
      
      // Enrich categories with detailed field objects for forms.
      // Feb 2026 (PM revamp) — prefer the backend's `description_columns`
      // when present so newly-select columns (with `type: "select"` and
      // `options[]`) render as searchable dropdowns. Falls through to
      // the legacy string-field path only when the backend didn't ship
      // description_columns for that category.
      const enrichedCategories = {};
      Object.entries(dbCategories).forEach(([code, config]) => {
        const descCols = config.description_columns || [];
        const backedFields = descCols.map(col => ({
          key: col.key,
          label: col.label || col.key,
          type: col.type || "text",
          required: !!col.required,
          options: col.options || [],
          include_in_name: !!col.include_in_name,
          order: col.order ?? 0,
          placeholder: col.placeholder || `Enter ${(col.label || col.key).toLowerCase()}`,
        }));

        const legacyFields = (config.fields || []).map(fieldKey => {
          const defaultField = DEFAULT_RM_CATEGORIES[code]?.fields?.find(f => f.key === fieldKey);
          if (defaultField) return defaultField;
          return {
            key: fieldKey,
            label: fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            required: config.nameFormat?.includes(fieldKey) || false,
            placeholder: `Enter ${fieldKey.replace(/_/g, ' ')}`,
            type: fieldKey.includes('weight') || fieldKey.includes('qty') ? 'number' : 'text'
          };
        });

        enrichedCategories[code] = {
          name: config.name,
          // Jul 2026 — carry the backend's `description` (falls back to
          // the DEFAULT_RM_CATEGORIES caption if the backend omits it,
          // e.g. before the categories collection is fully seeded).
          // The card caption reads from this field so Tech Ops can
          // edit it via PUT /api/production/rm-categories/{code} with
          // no code change.
          description: config.description || DEFAULT_RM_CATEGORIES[code]?.description || '',
          nameFormat: config.nameFormat || [],
          // Prefer the backend-shaped fields (with select/options) — legacy
          // string list is the fallback for categories without description_columns.
          fields: backedFields.length ? backedFields : legacyFields,
        };
      });
      
      // Store as list for dropdowns
      const categoriesList = Object.entries(enrichedCategories).map(([code, config]) => ({
        code,
        name: config.name,
        description: config.description || DEFAULT_RM_CATEGORIES[code]?.description || '',
        fields: config.fields,
        nameFormat: config.nameFormat
      }));
      setRmCategoriesList(categoriesList);
      
      // Merge with defaults to ensure critical categories are available
      setRmCategoriesMap({ ...DEFAULT_RM_CATEGORIES, ...enrichedCategories });
    } catch (error) {
      console.error("Failed to fetch master data:", error);
      setRmCategoriesMap(DEFAULT_RM_CATEGORIES);
      // Also set the list from defaults so the UI has data
      const defaultList = Object.entries(DEFAULT_RM_CATEGORIES).map(([code, config]) => ({
        code,
        name: config.name,
        description: config.description || '',
        fields: config.fields,
        nameFormat: config.nameFormat
      }));
      setRmCategoriesList(defaultList);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/demand-hub/my-requests/summary`);
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch summary");
    }
  };

  const fetchMyRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/demand-hub/my-requests`);
      setMyRequests(res.data);
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchBidsoSkus = async () => {
    try {
      let url = `${API}/demand-hub/bidso-skus?`;
      if (skuFilters.vertical_id) url += `vertical_id=${skuFilters.vertical_id}&`;
      if (skuFilters.model_id) url += `model_id=${skuFilters.model_id}&`;
      if (skuFilters.search) url += `search=${encodeURIComponent(skuFilters.search)}&`;
      
      const res = await axios.get(url);
      // Handle paginated response
      const data = res.data;
      setBidsoSkus(data.items || data || []);
    } catch (error) {
      console.error("Failed to fetch Bidso SKUs");
      setBidsoSkus([]);
    }
  };

  const handleSelectBidsoSku = async (sku) => {
    setSelectedBidso(sku);
    setBuyerSkuForm({ ...buyerSkuForm, bidso_sku_id: sku.bidso_sku_id });
    
    // Fetch existing Buyer SKUs for this Bidso SKU
    try {
      const res = await axios.get(`${API}/demand-hub/existing-buyer-skus/${sku.bidso_sku_id}`);
      setExistingBuyerSkus(res.data);
    } catch (error) {
      setExistingBuyerSkus({ existing: [], pending_requests: [] });
    }
  };

  const handleCreateBuyerSkuRequest = async () => {
    if (!buyerSkuForm.bidso_sku_id || !buyerSkuForm.brand_id) {
      toast.error("Please select a Bidso SKU and Brand");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/demand-hub/buyer-sku-requests`, buyerSkuForm);
      toast.success(`Request created for ${res.data.buyer_sku_id}`);
      setShowBuyerSkuDialog(false);
      setBuyerSkuForm({ bidso_sku_id: "", brand_id: "", notes: "" });
      setSelectedBidso(null);
      fetchSummary();
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    }
  };

  const handleCreateRmRequest = async () => {
    // Validate required fields
    const categoryConfig = rmCategoriesMap[rmForm.category];
    const missingFields = [];

    // Feb 2026 (PM revamp) — PM uses buyer_sku_links instead of brand_ids
    // (spec §Component 2). Both AC10 conditions enforced here:
    //   • zero linked Buyer SKUs → blocked with a validation message
    //   • any link with consumption <= 0 → blocked
    // Jul 2026 (BS revamp) — BS follows the same rule (spec Component 2).
    // Aug 2026 — LB was migrated to a `category_data.buyer_sku` field
    // (no brand tagging, no buyer_sku_links). The render branch already
    // returns null for LB's brands block, but the validation branch
    // used to fall through and demand "Brands" — every LB submission
    // failed. `NO_BRAND_TAGGING` (module scope) now guards BOTH sides
    // so the pair can't drift. LB's own required fields (Type + Buyer
    // SKU via `category_data`) are enforced by the generic
    // `field.required` loop that follows.
    if (rmForm.category === "PM" || rmForm.category === "BS") {
      const links = rmForm.buyer_sku_links || [];
      if (links.length === 0) {
        toast.error("Please link at least one Buyer SKU with a consumption > 0.");
        return;
      }
      const bad = links.find(l => !l.consumption || l.consumption <= 0);
      if (bad) {
        toast.error(`Consumption must be greater than 0 for ${bad.buyer_sku_id}.`);
        return;
      }
    } else if (!NO_BRAND_TAGGING.has(rmForm.category) && rmForm.brand_ids.length === 0) {
      missingFields.push("Brands");
    }
    
    // Check category-specific required fields
    categoryConfig.fields.forEach(field => {
      if (field.required && !rmForm.category_data[field.key]) {
        missingFields.push(field.label);
      }
    });
    
    if (missingFields.length > 0) {
      toast.error(`Please fill required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Jun 2026 — validate asset_link URL when present. The HTML5
    // type="url" input gives native UX but doesn't block submit on its
    // own. Empty is allowed; if filled, must start with http:// or https://.
    const rawAssetLink = (rmForm.category_data?.asset_link || "").toString().trim();
    if (rawAssetLink && !/^https?:\/\//i.test(rawAssetLink)) {
      toast.error("Asset Link must be a valid URL starting with http:// or https://");
      return;
    }
    
    // Auto-generate RM name from category_data based on nomenclature
    const generatedName = generateRmName(rmForm.category, rmForm.category_data, rmCategoriesMap);
    
    if (!generatedName) {
      toast.error("Could not generate RM name. Please fill all required fields.");
      return;
    }
    
    try {
      await axios.post(`${API}/rm-requests`, {
        ...rmForm,
        requested_name: generatedName,
        category_data: rmForm.category_data,
        artwork_files: rmForm.artwork_files
      });
      toast.success("RM request created");
      setShowRmDialog(false);
      setRmForm({ category: "LB", description: "", brand_ids: [], category_data: {}, artwork_files: [], buyer_sku_links: [] });
      fetchSummary();
      fetchMyRequests();
    } catch (error) {
      // Jul 2026 (Dedup fix — Component 3): the backend now returns
      // HTTP 409 with `detail = {duplicate, existing_rm_id,
      // existing_description, message}` when an identical RM already
      // exists. Surface the existing RM to the requester and KEEP the
      // form open (don't reset), so Demand can copy the ID and cancel
      // out of the dialog instead of retrying. Any other error keeps
      // the pre-existing behaviour.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail && typeof detail === "object" && detail.duplicate) {
        const msg =
          detail.message ||
          `An identical RM already exists: ${detail.existing_rm_id || ""}`;
        toast.error(`${msg}. Use that RM instead.`, {
          duration: 8000,
          id: `rm-dup-${detail.existing_rm_id || "unknown"}`,
        });
        // Do NOT close the dialog and do NOT clear the form.
        return;
      }
      toast.error(
        (typeof detail === "string" && detail) ||
          detail?.message ||
          "Failed to create request",
      );
    }
  };

  const handleRmCategoryChange = (newCategory) => {
    setRmForm({
      ...rmForm,
      category: newCategory,
      category_data: {}, // Reset category data when category changes
      artwork_files: [],  // Reset artwork files when category changes
      // Feb 2026 (PM/LB revamp) — Buyer-SKU linkage is category-scoped;
      // reset on switch so a PM/LB link never leaks into another category.
      buyer_sku_links: [],
    });
  };

  const updateCategoryData = (key, value) => {
    // Functional update — Bug fix Feb 2026 (P0). Model and Brand handlers
    // fire two updateCategoryData calls in one event (e.g. `model` + `model_id`
    // then `brand` + `brand_id`). With a non-functional `setRmForm(...rmForm)`,
    // the second call closed over the pre-first-call snapshot and clobbered
    // the first write, so Model/Brand snapped back to placeholder. Functional
    // form composes: prev is always the freshest state.
    setRmForm(prev => ({
      ...prev,
      category_data: {
        ...prev.category_data,
        [key]: value,
      },
    }));
  };

  // File upload handlers
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const allowedExtensions = ['.ai', '.pdf', '.cdr', '.eps', '.svg', '.psd'];
    const validFiles = Array.from(files).filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedExtensions.includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    setUploadingFiles(true);
    
    try {
      const formData = new FormData();
      validFiles.forEach(file => formData.append('files', file));
      
      const res = await axios.post(`${API}/uploads/rm-artwork`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.uploaded && res.data.uploaded.length > 0) {
        setRmForm({
          ...rmForm,
          artwork_files: [...rmForm.artwork_files, ...res.data.uploaded]
        });
        toast.success(`${res.data.uploaded.length} file(s) uploaded`);
      }
      
      if (res.data.errors && res.data.errors.length > 0) {
        res.data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
      }
    } catch (error) {
      toast.error("Failed to upload files");
    } finally {
      setUploadingFiles(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = async (storedName) => {
    try {
      await axios.delete(`${API}/uploads/rm-artwork/${storedName}`);
      setRmForm({
        ...rmForm,
        artwork_files: rmForm.artwork_files.filter(f => f.stored_name !== storedName)
      });
      toast.success("File removed");
    } catch (error) {
      toast.error("Failed to remove file");
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'PDF': return <FileText className="h-4 w-4 text-red-500" />;
      case 'AI': return <File className="h-4 w-4 text-orange-500" />;
      case 'CDR': return <File className="h-4 w-4 text-green-500" />;
      default: return <File className="h-4 w-4 text-gray-500" />;
    }
  };

  const getFilteredModels = () => {
    if (!skuFilters.vertical_id) return models;
    return models.filter(m => m.vertical_id === skuFilters.vertical_id);
  };

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || id;
  const getBrandCode = (id) => brands.find(b => b.id === id)?.code || "";

  // Check if brand already has a Buyer SKU or pending request
  const isBrandTaken = (brandId) => {
    const existing = existingBuyerSkus.existing.some(s => s.brand_id === brandId);
    const pending = existingBuyerSkus.pending_requests.some(r => r.brand_id === brandId);
    return existing || pending;
  };

  return (
    <div className="p-6 space-y-6" data-testid="demand-hub-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Demand Hub</h1>
          <p className="text-gray-500 mt-1">Request new Buyer SKUs and Raw Materials</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Requests</p>
                <p className="text-2xl font-bold">{summary.total_pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold">{summary.total_approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Box className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">SKU Requests</p>
                <p className="text-2xl font-bold">{summary.buyer_sku_requests?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Tag className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">RM Requests</p>
                <p className="text-2xl font-bold">{summary.rm_requests?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="buyer-sku" data-testid="buyer-sku-tab">
            <Box className="h-4 w-4 mr-2" />
            Request Buyer SKU
          </TabsTrigger>
          <TabsTrigger value="rm-request" data-testid="rm-request-tab">
            <Tag className="h-4 w-4 mr-2" />
            Request RM
          </TabsTrigger>
          <TabsTrigger value="my-requests" data-testid="my-requests-tab">
            <FileText className="h-4 w-4 mr-2" />
            My Requests
            {summary.total_pending > 0 && (
              <Badge className="ml-2 bg-orange-500">{summary.total_pending}</Badge>
            )}
          </TabsTrigger>
          {/* Jul 2026 — Import brand-specific BOM from another Buyer SKU.
              Sits after My Requests per spec §Component 4. */}
          <TabsTrigger value="import-brand-bom" data-testid="import-brand-bom-tab">
            <Copy className="h-4 w-4 mr-2" />
            Import Brand BOM
          </TabsTrigger>
        </TabsList>

        {/* Request Buyer SKU Tab */}
        <TabsContent value="buyer-sku" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Request New Buyer SKU</CardTitle>
              <CardDescription>
                Select a base product (Bidso SKU) and a brand to create a new branded variant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-[200px]">
                  <Label className="text-xs text-gray-500">Vertical</Label>
                  <Select 
                    value={skuFilters.vertical_id} 
                    onValueChange={(v) => setSkuFilters({ ...skuFilters, vertical_id: v === "all" ? "" : v, model_id: "" })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Verticals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Verticals</SelectItem>
                      {verticals.filter(v => v.id).map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.code} - {v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-[200px]">
                  <Label className="text-xs text-gray-500">Model</Label>
                  <Select 
                    value={skuFilters.model_id || "all"} 
                    onValueChange={(v) => setSkuFilters({ ...skuFilters, model_id: v === "all" ? "" : v })}
                    disabled={!skuFilters.vertical_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Models" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Models</SelectItem>
                      {getFilteredModels().filter(m => m.id).map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.code} - {m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-gray-500">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search Bidso SKU..."
                      value={skuFilters.search}
                      onChange={(e) => setSkuFilters({ ...skuFilters, search: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Bidso SKU Table */}
              <div className="border rounded-lg max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bidso SKU</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bidsoSkus.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          No Bidso SKUs found. Adjust filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      bidsoSkus.slice(0, 50).map(sku => (
                        <TableRow 
                          key={sku.bidso_sku_id} 
                          className={selectedBidso?.bidso_sku_id === sku.bidso_sku_id ? "bg-blue-50" : ""}
                        >
                          <TableCell>
                            <span className="font-mono font-medium">{sku.bidso_sku_id}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{sku.vertical?.code || "-"}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{sku.model?.code || "-"}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {sku.name || "-"}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant={selectedBidso?.bidso_sku_id === sku.bidso_sku_id ? "default" : "outline"}
                              onClick={() => handleSelectBidsoSku(sku)}
                              data-testid={`select-bidso-${sku.bidso_sku_id}`}
                            >
                              {selectedBidso?.bidso_sku_id === sku.bidso_sku_id ? "Selected" : "Select"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Selected SKU Info */}
              {selectedBidso && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">Selected: {selectedBidso.bidso_sku_id}</h3>
                        <p className="text-sm text-gray-600">{selectedBidso.name}</p>
                        
                        {/* Existing Buyer SKUs */}
                        {existingBuyerSkus.existing.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 font-medium">Existing Buyer SKUs:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {existingBuyerSkus.existing.map(s => (
                                <Badge key={s.buyer_sku_id} variant="secondary">
                                  {s.buyer_sku_id}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Pending Requests */}
                        {existingBuyerSkus.pending_requests.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 font-medium">Pending Requests:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {existingBuyerSkus.pending_requests.map(r => (
                                <Badge key={r.buyer_sku_id} className="bg-orange-100 text-orange-700">
                                  {r.buyer_sku_id}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Button onClick={() => setShowBuyerSkuDialog(true)} data-testid="open-buyer-sku-dialog">
                        <Plus className="h-4 w-4 mr-2" />
                        Request Buyer SKU
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Request RM Tab */}
        <TabsContent value="rm-request" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Request New Raw Material</CardTitle>
              <CardDescription>
                Request brand-specific raw materials like labels, packaging, or brand assets.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category Cards — Demand Hub Request RM is restricted
                    to brand-specific RM categories. The /api/rm-categories
                    endpoint still returns every category to its other
                    consumers (RM master, BOM editor, etc.); this filter
                    is purely a UI scoping decision for the Demand flow. */}
                {rmCategoriesList
                  .filter(cat => ARTWORK_CATEGORIES.includes(cat.code))
                  .map(cat => (
                  <Card 
                    key={cat.code} 
                    className={`cursor-pointer transition-all hover:border-primary ${rmForm.category === cat.code ? 'border-primary bg-primary/5' : ''}`}
                    onClick={() => setRmForm({ ...rmForm, category: cat.code })}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${rmForm.category === cat.code ? 'bg-primary text-white' : 'bg-gray-100'}`}>
                          {cat.code === "LB" && <Tag className="h-5 w-5" />}
                          {cat.code === "PM" && <Package className="h-5 w-5" />}
                          {cat.code === "BS" && <Layers className="h-5 w-5" />}
                          {cat.code === "STK" && <FileText className="h-5 w-5" />}
                        </div>
                        <div>
                          <h3 className="font-semibold">{cat.name}</h3>
                          <p className="text-sm text-gray-500">{cat.description}</p>
                        </div>
                        {rmForm.category === cat.code && (
                          <CheckCircle className="h-5 w-5 text-primary ml-auto" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setShowRmDialog(true)} data-testid="open-rm-dialog">
                  <Plus className="h-4 w-4 mr-2" />
                  Create RM Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Requests Tab */}
        <TabsContent value="my-requests" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Request Details</TableHead>
                    <TableHead>Brands</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : myRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No requests yet. Create your first request above.
                      </TableCell>
                    </TableRow>
                  ) : (
                    myRequests.map(req => (
                      <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                        <TableCell>
                          {req.type === "BUYER_SKU" ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              <Box className="h-3 w-3 mr-1" />
                              Buyer SKU
                            </Badge>
                          ) : req.type === "BIDSO_CLONE" ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <Copy className="h-3 w-3 mr-1" />
                              Clone SKU
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700">
                              <Tag className="h-3 w-3 mr-1" />
                              RM
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {req.type === "BUYER_SKU" ? (
                            <div>
                              <span className="font-mono font-medium">{req.buyer_sku_id}</span>
                              <p className="text-xs text-gray-500">Base: {req.bidso_sku_id}</p>
                            </div>
                          ) : req.type === "BIDSO_CLONE" ? (
                            <div>
                              <span className="font-medium">{req.proposed_name}</span>
                              <p className="text-xs text-gray-500">Source: {req.source_bidso_sku_id}</p>
                            </div>
                          ) : (
                            <div>
                              <span className="font-medium">{req.requested_name}</span>
                              <p className="text-xs text-gray-500">{req.category}</p>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {req.type === "BUYER_SKU" ? (
                              <Badge variant="secondary">{req.brand_code}</Badge>
                            ) : req.type === "BIDSO_CLONE" ? (
                              <Badge variant="secondary">{req.bom_modifications?.length || 0} mods</Badge>
                            ) : (
                              req.brands?.map(b => (
                                <Badge key={b.code} variant="secondary">{b.code}</Badge>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {req.status === "PENDING" && (
                            <Badge className="bg-orange-100 text-orange-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {req.status === "APPROVED" && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </Badge>
                          )}
                          {req.status === "REJECTED" && (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(req.requested_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {req.status === "APPROVED" && (
                            <div>
                              <span className="font-mono text-xs text-green-600 font-medium">
                                {req.type === "BUYER_SKU" ? req.buyer_sku_id : req.type === "BIDSO_CLONE" ? req.created_bidso_sku_id : req.created_rm_id}
                              </span>
                              {req.type === "BIDSO_CLONE" && req.created_rm_ids && req.created_rm_ids.length > 0 && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  +{req.created_rm_ids.length} new RM
                                </p>
                              )}
                            </div>
                          )}
                          {req.status === "REJECTED" && (
                            <div className="space-y-1">
                              {req.review_notes && (
                                <p className="text-xs text-red-600" title={req.review_notes}>
                                  {req.review_notes.substring(0, 40)}...
                                </p>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Import Brand BOM Tab (Jul 2026) — see components/ImportBrandBomTab.jsx. */}
        <TabsContent value="import-brand-bom" className="space-y-4">
          <ImportBrandBomTab
            buyerSkus={availableBuyerSkusForPm}
            brands={brands}
          />
        </TabsContent>
      </Tabs>

      {/* Buyer SKU Request Dialog */}
      <Dialog open={showBuyerSkuDialog} onOpenChange={setShowBuyerSkuDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Buyer SKU</DialogTitle>
            <DialogDescription>
              Create a branded variant of {selectedBidso?.bidso_sku_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Base Product (Bidso SKU)</Label>
              <Input value={selectedBidso?.bidso_sku_id || ""} disabled className="font-mono" />
            </div>
            
            <div>
              <Label>Brand *</Label>
              <Select 
                value={buyerSkuForm.brand_id} 
                onValueChange={(v) => setBuyerSkuForm({ ...buyerSkuForm, brand_id: v })}
              >
                <SelectTrigger data-testid="buyer-sku-brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(b => (
                    <SelectItem 
                      key={b.id} 
                      value={b.id}
                      disabled={isBrandTaken(b.id)}
                    >
                      {b.code} - {b.name}
                      {isBrandTaken(b.id) && " (Already exists)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {buyerSkuForm.brand_id && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium">Proposed Buyer SKU ID:</p>
                <p className="font-mono text-lg text-blue-700">
                  {getBrandCode(buyerSkuForm.brand_id)}_{selectedBidso?.bidso_sku_id}
                </p>
              </div>
            )}
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={buyerSkuForm.notes}
                onChange={(e) => setBuyerSkuForm({ ...buyerSkuForm, notes: e.target.value })}
                placeholder="Any additional notes for Tech Ops..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowBuyerSkuDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateBuyerSkuRequest}
                disabled={!buyerSkuForm.brand_id}
                data-testid="submit-buyer-sku-request"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RM Request Dialog */}
      <Dialog open={showRmDialog} onOpenChange={setShowRmDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request New Raw Material</DialogTitle>
            <DialogDescription>
              Request a {rmCategoriesMap[rmForm.category]?.name || rmForm.category} for Tech Ops to create.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Category Selection */}
            <div>
              <Label>Category *</Label>
              <Select value={rmForm.category || undefined} onValueChange={handleRmCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(rmCategoriesMap).filter(([code]) => code).map(([code, config]) => (
                    <SelectItem key={code} value={code}>{code} - {config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{rmCategoriesMap[rmForm.category]?.description}</p>
            </div>
            
            {/* Category-Specific Fields */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {rmCategoriesMap[rmForm.category]?.name} Details
                </h4>
                <span className="text-xs text-gray-400">
                  Naming: {rmCategoriesMap[rmForm.category]?.nameFormat?.join("_")}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {rmCategoriesMap[rmForm.category]?.fields?.map(field => (
                  <div key={field.key} className={field.key === 'specs' || field.key === 'buyer_sku' ? 'col-span-2' : ''}>
                    <Label className="text-xs">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    {(() => {
                      // Feb 2026 (PM revamp) — searchable dropdown for
                      // any `type: "select"` field OR any PM-specific
                      // master-list-backed field (model/brand). Values
                      // are stored as strings in category_data. For
                      // model/brand we also stash the id in a hidden
                      // sibling key so downstream can preserve referential
                      // integrity if needed.
                      const value = rmForm.category_data[field.key] || "";

                      // Master-list-backed fields on the PM category.
                      if (rmForm.category === "PM" && field.key === "model") {
                        return (
                          <PmSearchSelect
                            value={value}
                            onChange={(v, item) => {
                              updateCategoryData(field.key, v);
                              updateCategoryData("model_id", item?.id || "");
                            }}
                            // Feb 2026 (P1) — store the *name* (not code)
                            // as the selected value so `generateRmName`
                            // renders human-readable descriptions.
                            options={models.map(m => ({ value: m.name, label: `${m.code} - ${m.name}`, id: m.id }))}
                            placeholder="Select model…"
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      // Feb 2026 (BS Model) — optional model picker
                      // for Brand Assets. Mirrors PM's model branch:
                      // stores the *name* in category_data.model so
                      // generateRmName renders it, and stashes the id
                      // in category_data.model_id for downstream ref.
                      // Optional field — leaving it blank simply omits
                      // the segment from the generated name.
                      if (rmForm.category === "BS" && field.key === "model") {
                        return (
                          <PmSearchSelect
                            value={value}
                            onChange={(v, item) => {
                              updateCategoryData(field.key, v);
                              updateCategoryData("model_id", item?.id || "");
                            }}
                            options={models.map(m => ({ value: m.name, label: `${m.code} - ${m.name}`, id: m.id }))}
                            placeholder="Select model…"
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      if (rmForm.category === "PM" && field.key === "brand") {
                        return (
                          <PmSearchSelect
                            value={value}
                            onChange={(v, item) => {
                              const newBrandId = item?.id || "";
                              updateCategoryData(field.key, v);
                              updateCategoryData("brand_id", newBrandId);
                              // Feb 2026 (P1) — brand change resets any
                              // linked Buyer SKUs that don't belong to
                              // the new brand. Empty brand → keep all
                              // (legacy behaviour). Uses functional
                              // setter so it composes with the two
                              // updateCategoryData writes above.
                              setRmForm(prev => {
                                if (!newBrandId) return prev;
                                const kept = (prev.buyer_sku_links || []).filter(l => {
                                  const sku = availableBuyerSkusForPm.find(s => s.buyer_sku_id === l.buyer_sku_id);
                                  return !sku || sku.brand_id === newBrandId;
                                });
                                if (kept.length === (prev.buyer_sku_links || []).length) return prev;
                                return { ...prev, buyer_sku_links: kept };
                              });
                            }}
                            options={brands.map(b => ({ value: b.name, label: `${b.code} - ${b.name}`, id: b.id }))}
                            placeholder="Select brand…"
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      // Jul 2026 (BS revamp §Component 1 + §Component 2) —
                      // BS brand mirrors PM: optional dropdown that
                      // scopes the Buyer SKU linker to that brand and
                      // drops non-matching links when brand changes.
                      // Empty brand → show all Buyer SKUs.
                      if (rmForm.category === "BS" && field.key === "brand") {
                        return (
                          <PmSearchSelect
                            value={value}
                            onChange={(v, item) => {
                              const newBrandId = item?.id || "";
                              updateCategoryData(field.key, v);
                              updateCategoryData("brand_id", newBrandId);
                              setRmForm(prev => {
                                if (!newBrandId) return prev;
                                const kept = (prev.buyer_sku_links || []).filter(l => {
                                  const sku = availableBuyerSkusForPm.find(s => s.buyer_sku_id === l.buyer_sku_id);
                                  return !sku || sku.brand_id === newBrandId;
                                });
                                if (kept.length === (prev.buyer_sku_links || []).length) return prev;
                                return { ...prev, buyer_sku_links: kept };
                              });
                            }}
                            options={brands.map(b => ({ value: b.name, label: `${b.code} - ${b.name}`, id: b.id }))}
                            placeholder="Select brand…"
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      // Specs on PM — managed list with "Others → add".
                      if (rmForm.category === "PM" && field.key === "specs") {
                        return (
                          <PmSpecsSelect
                            value={value}
                            onChange={(v) => updateCategoryData(field.key, v)}
                            options={field.options || []}
                            onOptionsChange={(nextOpts) => {
                              // Locally patch the category's options so
                              // the newly-added value appears without a
                              // page refresh. Backend persistence is
                              // separately handled inside PmSpecsSelect.
                              setRmCategoriesMap(prev => {
                                const cat = prev[rmForm.category];
                                if (!cat) return prev;
                                return {
                                  ...prev,
                                  [rmForm.category]: {
                                    ...cat,
                                    fields: cat.fields.map(f =>
                                      f.key === "specs" ? { ...f, options: nextOpts } : f
                                    ),
                                  },
                                };
                              });
                            }}
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      // Jul 2026 (LB revamp §Component 2) — single searchable
                      // Buyer SKU picker + Consumption/UOM row. Selection
                      // populates category_data.buyer_sku (feeds the name)
                      // AND writes a single-element buyer_sku_links so the
                      // approval BOM-write path (category-agnostic) links
                      // the created label into the Buyer SKU's BOM.
                      if (rmForm.category === "LB" && field.key === "buyer_sku") {
                        const link = (rmForm.buyer_sku_links || [])[0] || null;
                        return (
                          <div className="space-y-2">
                            <PmSearchSelect
                              value={value}
                              onChange={(v) => {
                                updateCategoryData(field.key, v);
                                setRmForm(prev => {
                                  if (!v) return { ...prev, buyer_sku_links: [] };
                                  const cur = (prev.buyer_sku_links || [])[0] || {};
                                  return {
                                    ...prev,
                                    buyer_sku_links: [{
                                      buyer_sku_id: v,
                                      consumption: cur.consumption ?? 1,
                                      uom: cur.uom ?? "nos",
                                    }],
                                  };
                                });
                              }}
                              options={availableBuyerSkusForPm.map(s => ({
                                value: s.buyer_sku_id,
                                label: s.name ? `${s.buyer_sku_id} — ${s.name}` : s.buyer_sku_id,
                              }))}
                              placeholder="Select Buyer SKU…"
                              testId={`rm-field-${field.key}`}
                            />
                            {value && (
                              <div className="flex gap-3 items-end" data-testid="lb-consumption-row">
                                <div className="flex flex-col">
                                  <Label className="text-[10px] text-gray-500">Consumption *</Label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={link?.consumption ?? 1}
                                    onChange={(e) => {
                                      const c = parseFloat(e.target.value);
                                      setRmForm(prev => {
                                        const cur = (prev.buyer_sku_links || [])[0] || { buyer_sku_id: value, uom: "nos" };
                                        return { ...prev, buyer_sku_links: [{ ...cur, buyer_sku_id: value, consumption: isNaN(c) ? 0 : c }] };
                                      });
                                    }}
                                    className="h-9 w-32 px-2 text-sm border rounded-md"
                                    data-testid="lb-consumption-input"
                                  />
                                </div>
                                <div className="flex flex-col">
                                  <Label className="text-[10px] text-gray-500">UOM</Label>
                                  <input
                                    type="text"
                                    value={link?.uom ?? "nos"}
                                    onChange={(e) => {
                                      const u = e.target.value;
                                      setRmForm(prev => {
                                        const cur = (prev.buyer_sku_links || [])[0] || { buyer_sku_id: value, consumption: 1 };
                                        return { ...prev, buyer_sku_links: [{ ...cur, buyer_sku_id: value, uom: u }] };
                                      });
                                    }}
                                    className="h-9 w-24 px-2 text-sm border rounded-md"
                                    data-testid="lb-uom-input"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      // Generic `type: "select"` — searchable dropdown over the
                      // column's declared options.
                      if (field.type === "select" && (field.options || []).length > 0) {
                        return (
                          <PmSearchSelect
                            value={value}
                            onChange={(v) => updateCategoryData(field.key, v)}
                            options={(field.options || []).map(o => ({ value: o, label: o }))}
                            placeholder={`Select ${field.label.toLowerCase()}…`}
                            testId={`rm-field-${field.key}`}
                          />
                        );
                      }
                      // Fallback: plain text/number input.
                      return (
                        <Input
                          type={field.type || "text"}
                          value={value}
                          onChange={(e) => updateCategoryData(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="h-9 text-sm"
                          data-testid={`rm-field-${field.key}`}
                        />
                      );
                    })()}
                  </div>
                ))}
              </div>
              
              {/* Auto-generated RM Name Preview */}
              {generateRmName(rmForm.category, rmForm.category_data, rmCategoriesMap) && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Generated RM Name:</p>
                  <p className="font-mono text-sm text-blue-800 mt-1">
                    {generateRmName(rmForm.category, rmForm.category_data, rmCategoriesMap)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Feb 2026 (PM revamp) — For PM, replace the "For Brands"
                multi-select with a Buyer SKU multi-select + per-SKU
                consumption. Jul 2026 (LB revamp) — LB does not render a
                brands block at all; its single Buyer SKU picker +
                Consumption lives inline in the field grid above.
                Jul 2026 (BS revamp §Component 2) — BS uses the same
                PmBuyerSkuLinks linker as PM, brand-scoped when Brand
                is selected. Other categories keep the legacy brand tags. */}
            {(rmForm.category === "PM" || rmForm.category === "BS") ? (
              <PmBuyerSkuLinks
                links={rmForm.buyer_sku_links || []}
                onChange={(links) => setRmForm({ ...rmForm, buyer_sku_links: links })}
                buyerSkus={availableBuyerSkusForPm}
                brandId={rmForm.category_data?.brand_id || ""}
              />
            ) : NO_BRAND_TAGGING.has(rmForm.category) ? null : (
              <div>
                <Label>For Brands *</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg min-h-[50px] bg-white">
                  {rmForm.brand_ids.length === 0 && (
                    <span className="text-xs text-gray-400">No brands selected</span>
                  )}
                  {rmForm.brand_ids.map(bid => (
                    <Badge key={bid} variant="secondary" className="pr-1">
                      {getBrandCode(bid)} - {getBrandName(bid)}
                      <button
                        className="ml-1 hover:text-red-500"
                        onClick={() => setRmForm({
                          ...rmForm,
                          brand_ids: rmForm.brand_ids.filter(id => id !== bid)
                        })}
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <Select onValueChange={(v) => {
                  if (v && !rmForm.brand_ids.includes(v)) {
                    setRmForm({ ...rmForm, brand_ids: [...rmForm.brand_ids, v] });
                  }
                }}>
                  <SelectTrigger className="mt-2" data-testid="rm-brand-select">
                    <SelectValue placeholder="Add brand..." />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.filter(b => b.id && !rmForm.brand_ids.includes(b.id)).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Additional Notes */}
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={rmForm.description}
                onChange={(e) => setRmForm({ ...rmForm, description: e.target.value })}
                placeholder="Any additional specifications, artwork references, color codes..."
                rows={2}
                className="text-sm"
              />
            </div>
            
            {/* Artwork File Upload - Only for LB, BS, PM */}
            {ARTWORK_CATEGORIES.includes(rmForm.category) && (
              <div className="border rounded-lg p-4 bg-blue-50 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Artwork Files
                  </h4>
                  <span className="text-xs text-gray-500">Accepts: .ai, .pdf, .cdr, .eps, .svg, .psd</span>
                </div>
                
                {/* Uploaded Files List */}
                {rmForm.artwork_files.length > 0 && (
                  <div className="space-y-2">
                    {rmForm.artwork_files.map((file, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between bg-white p-2 rounded border"
                      >
                        <div className="flex items-center gap-2">
                          {getFileIcon(file.file_type)}
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px]">{file.original_name}</p>
                            <p className="text-xs text-gray-500">{file.file_type} • {file.size_display}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => window.open(`${API}/uploads/rm-artwork/${file.stored_name}`, '_blank')}
                            title="Download"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveFile(file.stored_name)}
                            title="Remove"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".ai,.pdf,.cdr,.eps,.svg,.psd"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="artwork-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFiles}
                    className="w-full"
                  >
                    {uploadingFiles ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Artwork Files
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1 text-center">Max 50MB per file</p>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowRmDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateRmRequest}
                data-testid="submit-rm-request"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemandHub;
